import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, FileText, Calendar, Shield, MessageCircle } from "lucide-react";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import RelatedResources from "../components/RelatedResources";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20TDS%20Filing%20in%20Coimbatore";

const FAQS = [
  { q: "Who is required to deduct TDS?", a: "Any business entity (proprietorship, partnership, company, LLP) that pays salary, rent above ₹2.4 lakh/year, contractor payments above ₹30,000 per bill (or ₹1 lakh aggregate per year), professional fees above ₹30,000, or commission must obtain TAN and deduct TDS at applicable rates." },
  { q: "What is TAN and is it mandatory?", a: "TAN (Tax Deduction and Collection Account Number) is a 10-digit alphanumeric number issued by the Income Tax Department. Anyone required to deduct TDS must obtain TAN. Quoting TAN on TDS challans, returns, and certificates is mandatory — penalty for not quoting is ₹10,000." },
  { q: "What are the penalties for late TDS payment and filing?", a: "Late deduction: 1% per month under Section 201(1A). Late payment: 1.5% per month. Late filing of return: ₹200 per day under Section 234E (capped at TDS amount). Failure to file at all: ₹10,000–₹1,00,000 under Section 271H. Use our TDS Penalty Calculator for an instant estimate." },
  { q: "Which TDS form should I file?", a: "Form 24Q for TDS on salary. Form 26Q for TDS on non-salary residents (rent, contractor, professional fees). Form 27Q for TDS on payments to non-residents. Form 27EQ for TCS (Tax Collected at Source)." },
  { q: "What are the due dates for TDS payment and filing?", a: "TDS payment: 7th of next month (30th April for March deductions). Quarterly TDS return filing: 31st July (Q1), 31st October (Q2), 31st January (Q3), 31st May (Q4). Form 16/16A: within 15 days of return filing." },
  { q: "Do I need to issue TDS certificates?", a: "Yes. Form 16 to employees by 15th June every year for the previous financial year. Form 16A to vendors/contractors quarterly within 15 days of TDS return filing. Failure to issue: ₹100 per day per certificate." },
  { q: "What if I deducted excess TDS by mistake?", a: "The deductee can claim the excess as refund while filing their ITR. Alternatively, you can file a TDS correction return through the TRACES portal to revise the deduction amount. We assist with TRACES corrections to ensure your ledger and Form 16/16A are accurate." },
];

const RELATED = [
  { to: "/tds-penalty-calculator", label: "TDS Penalty Calculator", description: "Section 201(1A) / 234E / 271H instant calc", tag: "Free Tool" },
  { to: "/itr-filing", label: "ITR Filing", description: "File your annual income tax return", tag: "Service" },
  { to: "/income-tax-calculator", label: "Income Tax Calculator", description: "Old vs New regime FY 2024-25 & 2025-26", tag: "Free Tool" },
  { to: "/gst-return-filing", label: "GST Return Filing", description: "Monthly GST compliance", tag: "Service" },
  { to: "/business-registration", label: "Business Registration", description: "Proprietorship, LLP, Pvt Ltd setup", tag: "Service" },
  { to: "/compliance-roadmap", label: "Business Compliance Roadmap", description: "Free 60-second compliance check", tag: "Tool" },
];

const TDSFilingPage = () => {
  useCanonical("/tds-filing");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "TDS Filing and Compliance in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Complete TDS compliance including quarterly returns, certificates, and reconciliation services."
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <>
      <Helmet>
        <title>TDS Filing in Coimbatore | Complete TDS Compliance | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Complete TDS compliance services in Coimbatore. Quarterly returns, TDS certificates, reconciliation. Stay penalty-free with WINTRUST SOLUTIONS." />
      </Helmet>
      {/* Hero */}
      <section className="bg-navy py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <nav className="text-slate-400 text-sm mb-4">
              <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">TDS Filing</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
              TDS Filing & Compliance in Coimbatore
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Managing Tax Deducted at Source (TDS) is a critical compliance task for any business making specified payments. WINTRUST SOLUTIONS provides end-to-end TDS filing and compliance services in Coimbatore, ensuring your business adheres to all regulations.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary">Manage Your TDS Compliance</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Complete TDS Compliance and Management
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: <FileText size={24} />, title: "TDS Calculation", desc: "Accurate calculation of TDS on payments like salaries, rent, professional fees, and contractor payments." },
              { icon: <Calendar size={24} />, title: "Quarterly Returns", desc: "Timely filing of quarterly TDS returns (Form 24Q for salary, 26Q for non-salary deductions)." },
              { icon: <Shield size={24} />, title: "TDS Certificates", desc: "Generation and issuance of TDS certificates (Form 16, Form 16A) for your employees and vendors." },
              { icon: <CheckCircle size={24} />, title: "Notice Handling", desc: "Expert handling of TDS-related notices, queries, and assessments from the Income Tax Department." },
            ].map((item, index) => (
              <div key={index} className="flex gap-4 p-6 bg-slate-50 rounded-lg">
                <div className="text-gold flex-shrink-0">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TDS Return Types */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            TDS Return Forms We Handle
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { form: "Form 24Q", desc: "Quarterly statement for TDS on salary" },
              { form: "Form 26Q", desc: "Quarterly statement for TDS on non-salary payments" },
              { form: "Form 27Q", desc: "Quarterly statement for TDS on payments to non-residents" },
              { form: "Form 27EQ", desc: "Quarterly statement for Tax Collected at Source (TCS)" },
            ].map((item, index) => (
              <div key={index} className="p-6 bg-white rounded-lg shadow-sm text-center">
                <h3 className="text-2xl font-bold text-gold mb-2">{item.form}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Due Dates */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            TDS Filing Due Dates
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-navy text-white">
                  <th className="p-4 text-left">Quarter</th>
                  <th className="p-4 text-left">Period</th>
                  <th className="p-4 text-left">Due Date for Payment</th>
                  <th className="p-4 text-left">Due Date for Filing</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { quarter: "Q1", period: "April - June", payment: "7th of following month", filing: "31st July" },
                  { quarter: "Q2", period: "July - September", payment: "7th of following month", filing: "31st October" },
                  { quarter: "Q3", period: "October - December", payment: "7th of following month", filing: "31st January" },
                  { quarter: "Q4", period: "January - March", payment: "30th April (March: 7th May)", filing: "31st May" },
                ].map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                    <td className="p-4 font-semibold text-navy">{row.quarter}</td>
                    <td className="p-4 text-slate-600">{row.period}</td>
                    <td className="p-4 text-slate-600">{row.payment}</td>
                    <td className="p-4 text-slate-600">{row.filing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Missed Deadline Alert */}
          <div className="mt-10 bg-amber-100 border-l-4 border-amber-500 rounded-lg p-6 md:p-8 shadow-sm" data-testid="tds-missed-deadline-alert">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="flex-1">
                <p className="text-navy font-semibold text-base md:text-lg">
                  Missed a TDS deadline? Act now — late filing attracts ₹200/day penalty. WhatsApp us immediately for notice handling and regularisation support.
                </p>
              </div>
              <a
                href="https://wa.me/918940988776?text=Hi%2C%20I%20need%20urgent%20TDS%20help"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp flex-shrink-0 whitespace-nowrap"
                data-testid="tds-missed-deadline-btn"
              >
                <MessageCircle size={18} /> Fix TDS Issue — WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gold py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-4 font-heading">
            Stay TDS Compliant with Expert Support
          </h2>
          <p className="text-navy/80 mb-6 max-w-2xl mx-auto">
            Don't let TDS compliance become a burden. Let WINTRUST SOLUTIONS handle your TDS filing while you focus on your business.
          </p>
          <a href="#contact" className="bg-navy text-white px-8 py-3 rounded-md font-semibold hover:bg-navy-900 transition-colors">
            Get Started
          </a>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection items={FAQS} testId="tds-fil-faq" />

      {/* Related Resources */}
      <RelatedResources
        title="Related Tools & Services"
        subtitle="Stay TDS compliant with these free tools"
        items={RELATED}
        testId="tds-fil-related"
      />

      <ContactForm />
    </>
  );
};

export default TDSFilingPage;
